// src/utils/tripStorage.js
// IndexedDB storage for travel trips with photos
// UPDATED: Added getAllTripsMeta() for fast list loading without photo data
// UPDATED: Generates small thumbnails on save for fast list display

const DB_NAME = 'TravelTrackerDB';
const DB_VERSION = 1;
const STORE_NAME = 'trips';

// 🔹 NEW: Maximum trips to keep (older trips auto-archived)
const MAX_TRIPS = 50;

// 🔹 NEW: Thumbnail settings
const THUMBNAIL_SIZE = 300; // px (increased for crisp display)
const THUMBNAIL_QUALITY = 0.75;

/**
 * Initialize IndexedDB
 */
function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create trips store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('savedAt', 'savedAt', { unique: false });
        store.createIndex('name', 'name', { unique: false });
      }
    };
  });
}

/**
 * 🔹 NEW: Generate a small thumbnail from a photo URL
 * Returns a small base64 JPEG for fast list display
 */
async function generateThumbnail(photoUrl, maxSize = THUMBNAIL_SIZE, quality = THUMBNAIL_QUALITY) {
  return new Promise((resolve) => {
    if (!photoUrl) {
      resolve(null);
      return;
    }
    
    const img = new Image();
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        
        // Calculate dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to small JPEG
        const thumbnail = canvas.toDataURL('image/jpeg', quality);
        resolve(thumbnail);
      } catch (err) {
        console.warn('[Thumbnail] Failed to generate:', err);
        resolve(null);
      }
    };
    
    img.onerror = () => {
      resolve(null);
    };
    
    // Set crossOrigin for remote URLs
    if (photoUrl.startsWith('http')) {
      img.crossOrigin = 'anonymous';
    }
    
    img.src = photoUrl;
  });
}

/**
 * 🔹 NEW: Extract the first usable photo URL from a trip
 */
function getFirstPhotoUrl(trip) {
  if (!trip?.steps) return null;
  
  for (const step of trip.steps) {
    const photos = step.photos || [];
    for (const photo of photos) {
      const url = photo.url || photo.src;
      if (url) return url;
    }
  }
  return null;
}

/**
 * Save a trip to IndexedDB
 * @param {Object} trip - Trip object with name, steps, photos, etc.
 * @returns {Promise<string>} Trip ID
 */
export async function saveTrip(trip) {
  try {
    const db = await initDB();
    
    // Generate ID from trip name if not present
    if (!trip.id) {
      trip.id = slugifyName(trip.name);
    }
    
    // Add timestamp
    trip.savedAt = trip.savedAt || Date.now();
    trip.updatedAt = Date.now();
    
    // 🔹 Generate thumbnail if we don't have one or if it's stale
    let thumbnail = trip._meta?.thumbnail || null;
    if (!thumbnail) {
      const firstPhotoUrl = getFirstPhotoUrl(trip);
      if (firstPhotoUrl) {
        thumbnail = await generateThumbnail(firstPhotoUrl);
      }
    }
    
    // 🔹 Store metadata separately for fast list loading
    trip._meta = {
      photoCount: countPhotosInTrip(trip),
      stepCount: trip.steps?.length || 0,
      hasAlbum: !!trip.albumData,
      albumSaved: trip.albumData?.isSaved || false,
      thumbnail: thumbnail, // 🔹 Store the generated thumbnail
    };
    
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    await new Promise((resolve, reject) => {
      const request = store.put(trip);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    db.close();
    return trip.id;
  } catch (error) {
    console.error('Failed to save trip:', error);
    throw error;
  }
}

/**
 * 🔹 NEW: Get only trip metadata for list display (FAST - no photo data loaded)
 * This prevents memory crashes when user has many trips
 * @returns {Promise<Array>} Array of trip metadata objects
 */
export async function getAllTripsMeta() {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const tripsMeta = [];
    
    // Use cursor to iterate without loading all data into memory
    await new Promise((resolve, reject) => {
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const trip = cursor.value;
          
          // Extract only metadata - NOT the heavy photo data
          tripsMeta.push({
            id: trip.id,
            name: trip.name || 'Untitled Trip',
            savedAt: trip.savedAt,
            updatedAt: trip.updatedAt,
            // Use cached meta if available, otherwise compute (slower but works for old data)
            photoCount: trip._meta?.photoCount ?? countPhotosInTrip(trip),
            stepCount: trip._meta?.stepCount ?? (trip.steps?.length || 0),
            hasAlbum: trip._meta?.hasAlbum ?? !!trip.albumData,
            albumSaved: trip._meta?.albumSaved ?? (trip.albumData?.isSaved || false),
            // 🔹 Use cached thumbnail from _meta
            thumbnail: trip._meta?.thumbnail || null,
          });
          
          cursor.continue();
        } else {
          resolve();
        }
      };
      
      request.onerror = () => reject(request.error);
    });
    
    db.close();
    
    // Sort by most recently updated
    return tripsMeta.sort((a, b) => (b.updatedAt || b.savedAt || 0) - (a.updatedAt || a.savedAt || 0));
  } catch (error) {
    console.error('Failed to get trips meta:', error);
    return [];
  }
}

/**
 * 🔹 NEW: Generate thumbnails for trips that don't have them
 * Call this once to backfill existing trips
 */
export async function generateMissingThumbnails() {
  try {
    const meta = await getAllTripsMeta();
    const tripsNeedingThumbnails = meta.filter(t => !t.thumbnail);
    
    if (tripsNeedingThumbnails.length === 0) {
      console.log('[Thumbnails] All trips have thumbnails');
      return 0;
    }
    
    console.log(`[Thumbnails] Generating thumbnails for ${tripsNeedingThumbnails.length} trips...`);
    
    let generated = 0;
    for (const tripMeta of tripsNeedingThumbnails.slice(0, 10)) { // Limit to 10 at a time
      try {
        const trip = await getTrip(tripMeta.id);
        if (trip) {
          const firstPhotoUrl = getFirstPhotoUrl(trip);
          if (firstPhotoUrl) {
            const thumbnail = await generateThumbnail(firstPhotoUrl);
            if (thumbnail) {
              trip._meta = trip._meta || {};
              trip._meta.thumbnail = thumbnail;
              await saveTrip(trip);
              generated++;
            }
          }
        }
      } catch (e) {
        console.warn('[Thumbnails] Failed for trip:', tripMeta.id, e);
      }
    }
    
    console.log(`[Thumbnails] Generated ${generated} thumbnails`);
    return generated;
  } catch (error) {
    console.error('Failed to generate thumbnails:', error);
    return 0;
  }
}

/**
 * Get all trips from IndexedDB (FULL DATA - use sparingly!)
 * ⚠️ WARNING: This loads ALL trip data including photos. 
 * For trip lists, use getAllTripsMeta() instead.
 * @returns {Promise<Array>} Array of trip objects
 */
export async function getAllTrips() {
  console.warn('[tripStorage] getAllTrips() loads full data - consider using getAllTripsMeta() for lists');
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const trips = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    db.close();
    
    // Sort by most recently updated
    return trips.sort((a, b) => (b.updatedAt || b.savedAt || 0) - (a.updatedAt || a.savedAt || 0));
  } catch (error) {
    console.error('Failed to get trips:', error);
    return [];
  }
}

/**
 * Get a single trip by ID
 * @param {string} id - Trip ID
 * @returns {Promise<Object|null>} Trip object or null
 */
export async function getTrip(id) {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const trip = await new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    db.close();
    return trip || null;
  } catch (error) {
    console.error('Failed to get trip:', error);
    return null;
  }
}

/**
 * Delete a trip from IndexedDB
 * @param {string} id - Trip ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteTrip(id) {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    db.close();
    return true;
  } catch (error) {
    console.error('Failed to delete trip:', error);
    return false;
  }
}

/**
 * 🔹 NEW: Get trip count without loading data
 * @returns {Promise<number>} Number of trips
 */
export async function getTripCount() {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const count = await new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    db.close();
    return count;
  } catch (error) {
    console.error('Failed to get trip count:', error);
    return 0;
  }
}

/**
 * 🔹 NEW: Delete oldest trips to stay under limit
 * @param {number} keepCount - Number of trips to keep (default MAX_TRIPS)
 * @returns {Promise<number>} Number of trips deleted
 */
export async function pruneOldTrips(keepCount = MAX_TRIPS) {
  try {
    const meta = await getAllTripsMeta();
    
    if (meta.length <= keepCount) {
      return 0; // Nothing to prune
    }
    
    // meta is already sorted newest-first, so slice off the ones to delete
    const toDelete = meta.slice(keepCount);
    
    let deleted = 0;
    for (const trip of toDelete) {
      const success = await deleteTrip(trip.id);
      if (success) deleted++;
    }
    
    console.log(`[tripStorage] Pruned ${deleted} old trips (kept ${keepCount})`);
    return deleted;
  } catch (error) {
    console.error('Failed to prune trips:', error);
    return 0;
  }
}

/**
 * Check if IndexedDB is supported and available
 * @returns {boolean}
 */
export function isIndexedDBSupported() {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

/**
 * Get storage usage estimate (if available)
 * @returns {Promise<Object>} Storage estimate
 */
export async function getStorageEstimate() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
        usagePercent: estimate.quota ? ((estimate.usage / estimate.quota) * 100).toFixed(2) : 0
      };
    }
  } catch (error) {
    console.error('Failed to get storage estimate:', error);
  }
  return { usage: 0, quota: 0, usagePercent: 0 };
}

/**
 * Helper: Slugify trip name for ID
 */
function slugifyName(name) {
  return String(name || 'untitled')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * 🔹 Helper to count photos in a trip without loading them
 */
function countPhotosInTrip(trip) {
  if (!trip?.steps) return 0;
  return trip.steps.reduce((count, step) => count + (step.photos?.length || 0), 0);
}

/**
 * Export trip as JSON file (fallback for sharing/backup)
 * @param {Object} trip - Trip object
 */
export function exportTripAsJSON(trip) {
  const json = JSON.stringify(trip, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trip-${slugifyName(trip.name)}-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Import trip from JSON file
 * @param {File} file - JSON file
 * @returns {Promise<Object>} Trip object
 */
export async function importTripFromJSON(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const trip = JSON.parse(e.target.result);
        resolve(trip);
      } catch (error) {
        reject(new Error('Invalid JSON file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}